import { useEffect } from "react";

/**
 * Runtime favicon swapping for branded shells (white-label portal, brand
 * dashboard).
 *
 * Why not a Helmet `<link rel="icon">`: index.html already declares the
 * Reveshare icon as `type="image/svg+xml"`, and Helmet only *adds* a second
 * link. Given two icons Chrome picks the scalable SVG, so the brand's PNG
 * never showed even though the title (which Helmet does replace) did. The fix
 * is to own the one `<link rel="icon">` ourselves: replace its node (Chrome
 * ignores an `href` change on an existing link) and let the browser sniff the
 * type, since uploads are restricted to PNG/JPEG/WebP.
 *
 * Several shells can want the icon at once — the host-level head and the
 * affiliate shell both render PortalBrandingHead — so requests form a stack:
 * the most recently mounted wins, and unmounting hands back to the previous
 * one, or to the Reveshare default when none is left.
 */
type Request = { id: number; href: string };

const requests: Request[] = [];
let nextId = 0;
let original: { href: string; type: string | null } | null | undefined;

function iconLinks(): HTMLLinkElement[] {
  return Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
  );
}

function rememberOriginal() {
  if (original !== undefined) return;
  const first = iconLinks()[0];
  original = first
    ? { href: first.getAttribute("href") ?? "", type: first.getAttribute("type") }
    : null;
}

function render() {
  rememberOriginal();
  const top = requests[requests.length - 1];
  const target = top ? { href: top.href, type: null } : original;

  for (const link of iconLinks()) link.remove();
  if (!target) return;

  const link = document.createElement("link");
  link.rel = "icon";
  link.href = target.href;
  if (target.type) link.type = target.type;
  document.head.appendChild(link);
}

/** Show `href` as the tab icon while the calling component is mounted with a non-null value. */
export function useFavicon(href: string | null | undefined): void {
  useEffect(() => {
    if (!href) return;
    const request = { id: nextId++, href };
    requests.push(request);
    render();
    return () => {
      const index = requests.indexOf(request);
      if (index !== -1) requests.splice(index, 1);
      render();
    };
  }, [href]);
}
